import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpUrlEncodingCodec } from '@angular/common/http';
import { Observable } from 'rxjs';
import { mergeMap, map} from 'rxjs/operators';
import { SelectItem } from 'primeng/primeng';
import { DatePipe } from '@angular/common';
import { environment } from '../../../environments/environment';
import { InitiativeService } from './initiative.service';
import { IAnnualPlanPriority } from './IAnnualPlanPriority';
import { AppLoadingService } from '../../app-loading-panel/app-loading.service';
import { IPrioritySort } from './IPrioritySort';
import { StrategiesDataStoreService } from '../../shared/services/strategies-data-store.service';
import { StrtPriority } from '../../shared/models/plan/StrtPriority';
import * as _ from 'lodash';
import {take} from 'rxjs/internal/operators';


@Injectable()
export class PriorityService {
  private baseUrl = environment.baseUrl;

  private static mapPrioritiesDates(priorities: StrtPriority[]) {
    priorities.forEach(PriorityService.mapPriorityDates);
    return priorities;
  }

  private static mapPriorityDates(priority: StrtPriority) {
    priority.objectives.forEach(o => o.initiatives.forEach(InitiativeService.mapInitiativeDates));
    return priority;
  }

  static getPrioritySelectItems(priorities: StrtPriority[]): SelectItem[] {
    return priorities.map(priority => <SelectItem>{label: priority.title, value: priority.priorityId});
  }

  constructor(private http: HttpClient,
              private datePipe: DatePipe,
              private _appLoadingService: AppLoadingService,
              private _strtDataStore: StrategiesDataStoreService) { }

  // retrieve priorities populated through objectives
  getPrioritiesObjectives(strategyId: number): Observable<StrtPriority[]> {
    return this.http.get(`${this.baseUrl}/strategies/${strategyId}/plan/priorities/withobjectives`).pipe(
      map(PriorityService.mapPrioritiesDates));
  }

  // retrieve priorities populated through outcomes
  getPrioritiesOutcomes(strategyId: number): Observable<StrtPriority[]> {
    return this.http.get<StrtPriority[]>(`${this.baseUrl}/strategies/${strategyId}/plan/priorities/withinitiatives`).pipe(
      map(PriorityService.mapPrioritiesDates));
  }

  fetchPriorities(strategyId: number): void {
    this.http.get<StrtPriority[]>(`${this.baseUrl}/strategies/${strategyId}/plan/priorities/withinitiatives?includeKeyMeasures=true`)
      .pipe(map(PriorityService.mapPrioritiesDates))
      .subscribe((priorities: StrtPriority[]) => {
        _.orderBy(priorities, ['sort'], ['asc']);
        this._strtDataStore.priorities = priorities;
      });
  }


  getCopyInfo(strategyId: number): Observable<StrtPriority[]> {
      return this.http.get<StrtPriority[]>(`${this.baseUrl}/strategies/${strategyId}/plan/priorities/withinitiatives?includeKeyMeasures=true`)
        .pipe(map(PriorityService.mapPrioritiesDates));
  }

  getAnnualPlanPriorities(strategyId: number, annualPlanStartDate?: Date, annualPlanEndDate?: Date): void {
    const startDateString = this.datePipe.transform(annualPlanStartDate, 'yyyy-MM-dd');
    const endDateString = this.datePipe.transform(annualPlanEndDate, 'yyyy-MM-dd');
    const requestOptions = this.createRequestOptions({});
    if (annualPlanStartDate) {
      requestOptions.params = requestOptions.params.set('annualPlanStartDate', startDateString);
    }
    if (annualPlanEndDate) {
      requestOptions.params = requestOptions.params.set('annualPlanEndDate', endDateString);
    }
    this.http.get<IAnnualPlanPriority[]>(`${this.baseUrl}/strategies/${strategyId}/plan/annual-plan/objectives`, requestOptions)
      .pipe(take(1))
      .subscribe((priorities: IAnnualPlanPriority[]) => {
        this._strtDataStore.AIPPriorities = priorities;
      });
  }

  getPlanInitiativesWithActivities(strategyId: number) {
    this.getDatedInitiativesWithActivities(strategyId);
  }

  getDatedInitiativesWithActivities(strategyId: number, initiativeStartDate?: Date, initiativeEndDate?: Date) {
    this._appLoadingService.setLoading();
    const startDateString = this.datePipe.transform(initiativeStartDate, 'yyyy-MM-dd');
    const endDateString = this.datePipe.transform(initiativeEndDate, 'yyyy-MM-dd');
    const requestOptions = this.createRequestOptions({});
    if (initiativeStartDate) {
      requestOptions.params = requestOptions.params.set('initiativeStartDate', startDateString);
    }
    if (initiativeEndDate) {
      requestOptions.params = requestOptions.params.set('initiativeEndDate', endDateString);
    }
    this.http.get<StrtPriority[]>
    (`${this.baseUrl}/strategies/${strategyId}/plan/priorities/withactivities`, requestOptions).pipe(
      map(PriorityService.mapPrioritiesDates)).subscribe((priorities: StrtPriority[]) => {
      this._strtDataStore.datedInitiativesWithActivities = priorities;
      this._appLoadingService.clearLoading();
    }, (err) => {
      console.error('Caught error fetching plan initiatives with activities');
      this._appLoadingService.clearLoading();
    });
  }

  createPriority(strategyId: number, priority: StrtPriority): Observable<StrtPriority[]> {
     return this.http.post<StrtPriority[]>(`${this.baseUrl}/strategies/${strategyId}/priorities`, priority).pipe(
      mergeMap(() => this.getPrioritiesObjectives(strategyId)));
  }

  updatePriority(strategyId: number, priority: StrtPriority): Observable<StrtPriority[]> {
    return this.http.put<StrtPriority[]>(`${this.baseUrl}/strategies/${strategyId}/priorities/${priority.priorityId}`, priority).pipe(
    mergeMap(() => this.getPrioritiesObjectives(strategyId)));
  }

  deletePriority(strategyId: number, priorityId: number): Observable<StrtPriority[]> {
    return this.http.delete<StrtPriority[]>(`${this.baseUrl}/strategies/${strategyId}/priorities/${priorityId}`).pipe(
    mergeMap(() => this.getPrioritiesObjectives(strategyId)));
  }

  private createRequestOptions(paramsObj?: {[key: string]: string}) {
    const params: HttpParams = paramsObj
      ? Object.keys(paramsObj).reduce(
        (previous, key) => previous.set(key, paramsObj[key]),
        new HttpParams({ encoder: new HttpUrlEncodingCodec()}))
      : undefined;
    return { params };
  }

  sortPriorities(prioritySortInfo: IPrioritySort): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/strategies/${prioritySortInfo.strategyId}/priorities/sort`, prioritySortInfo);
  }




}
